import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { WarehousesService, WarehousePagedResult } from '../../../services/warehouses.service';
import { PaginationOptions } from '../../../shared/interfaces/pagination.interface';
import { Warehouse } from '../../../models/Warehouse';
import { StatusService } from '../../../services/status.service';
import { Status } from '../../../models/Status';

@Component({
  selector: 'app-warehouses',
  templateUrl: './warehouses.component.html',
  styleUrls: ['./warehouses.component.css']
})
export class WarehousesComponent implements OnInit {
  // Configuração da tabela
  tableTitle = 'Gestão de Almoxarifados';
  showSearch = true;
  searchPlaceholder = 'Buscar almoxarifado...';
  clickable = true;

  // Colunas/Ações
  columns = [
    { titulo: 'Código', label: 'code' },
    { titulo: 'Nome', label: 'name' },
    { titulo: 'Tipo', label: 'type' },
    { titulo: 'Departamento', label: 'department' },
    { titulo: 'Status', label: 'status' }
  ];

  actions = [
    {
      label: 'Editar',
      icon: 'fas fa-pen-to-square',
      action: 'edit',
      class: 'btn-edit'
    },
    {
      label: 'Excluir',
      icon: 'fas fa-trash-can',
      action: 'delete',
      class: 'btn-delete'
    }
  ];

  warehouses: Warehouse[] = [];
  filteredWarehouses: Warehouse[] = [];
  isLoading = false;
  error: string | null = null;
  
  // Paginação
  currentPage = 1;
  pageSize = 50;
  totalItems = 0;
  totalPages = 0;
  hasNextPage = false;
  hasPreviousPage = false;

  // Filtros
  selectedStatus: string | null = '';
  selectedType: string | null = '';
  searchTerm: string = '';
  statusOptions: Status[] = [];
  typeOptions: string[] = [
    'Principal (Sede)',
    'Filial',
    'Oficina',
    'Showroom',
    'Depósito de Peças',
    'Almoxarifado de Segurança'
  ];

  constructor(
    private router: Router, 
    private warehousesService: WarehousesService,
    private statusService: StatusService
  ) {}

  ngOnInit(): void {
    this.loadStatuses();
    this.loadWarehouses();
  }

  loadStatuses(): void {
    this.statusService.getByEntity('Warehouse').subscribe({
      next: (statuses) => {
        this.statusOptions = statuses;
      },
      error: (err) => {
        console.error('Erro ao carregar status', err);
        this.statusOptions = [];
      }
    });
  }

  loadWarehouses(): void {
    this.isLoading = true;
    this.error = null;

    const options: PaginationOptions = { page: this.currentPage, pageSize: this.pageSize };

    this.warehousesService.getAll(options).subscribe({
      next: (result: WarehousePagedResult) => {
        this.warehouses = result.items || [];
        this.filteredWarehouses = [...this.warehouses];
        this.totalItems = result.totalItems;
        this.totalPages = result.totalPages;
        this.hasNextPage = this.currentPage < this.totalPages;
        this.hasPreviousPage = this.currentPage > 1;
        this.currentPage = result.page;
        this.pageSize = result.pageSize;
        this.isLoading = false;
      },
      error: (error) => {
        this.error = `Erro ao carregar almoxarifados: ${error.message || 'Erro desconhecido'}`;
        this.isLoading = false;
        this.warehouses = [];
        this.filteredWarehouses = [];
      }
    });
  }

  onStatusFilterChange(status: string | null): void {
    this.selectedStatus = status;
    this.applyFilters();
  }

  onTypeFilterChange(type: string | null): void {
    this.selectedType = type;
    this.applyFilters();
  }

  clearFilters(): void {
    this.selectedStatus = '';
    this.selectedType = '';
    this.searchTerm = '';
    this.loadWarehouses();
  }

  private applyFilters(): void {
    this.isLoading = true;
    this.error = null;
    this.currentPage = 1;

    const hasStatus = !!(this.selectedStatus && this.selectedStatus.trim());
    const hasType   = !!(this.selectedType   && this.selectedType.trim());
    const hasSearch = !!(this.searchTerm     && this.searchTerm.trim());

    const applyClientSide = (items: Warehouse[]) => {
      let result = items;
      if (hasType)   result = result.filter(w => (w.type || '').toLowerCase().trim() === this.selectedType!.toLowerCase().trim());
      if (hasStatus) result = result.filter(w => (w.status || '').toLowerCase().trim() === this.selectedStatus!.toLowerCase().trim());
      if (hasSearch) result = result.filter(w => [w.name, w.code, w.type, w.department].some(v => v && String(v).toLowerCase().includes(this.searchTerm.toLowerCase())));
      this.filteredWarehouses = result;
      this.totalItems = result.length;
      this.totalPages = 1;
      this.hasNextPage = false;
      this.hasPreviousPage = false;
      this.currentPage = 1;
      this.isLoading = false;
    };

    // Both filters active: load from the more-specific one and cross-filter client-side
    if (hasType && hasStatus) {
      this.warehousesService.getByType(this.selectedType!).subscribe({
        next: (items) => applyClientSide(items || []),
        error: (err) => { this.error = err.message || 'Erro ao filtrar por tipo'; this.isLoading = false; this.filteredWarehouses = []; }
      });
      return;
    }

    if (hasType) {
      this.warehousesService.getByType(this.selectedType!).subscribe({
        next: (items) => applyClientSide(items || []),
        error: (err) => { this.error = err.message || 'Erro ao filtrar por tipo'; this.isLoading = false; this.filteredWarehouses = []; }
      });
      return;
    }

    if (hasStatus) {
      this.warehousesService.getByStatus(this.selectedStatus!).subscribe({
        next: (items) => applyClientSide(items || []),
        error: (err) => { this.error = err.message || 'Erro ao filtrar por status'; this.isLoading = false; this.filteredWarehouses = []; }
      });
      return;
    }

    // No filter — load paged from backend
    const options: PaginationOptions = { page: this.currentPage, pageSize: this.pageSize };
    this.warehousesService.getAll(options).subscribe({
      next: (result: WarehousePagedResult) => {
        this.warehouses = result.items || [];
        this.filteredWarehouses = hasSearch
          ? this.warehouses.filter(w => [w.name, w.code, w.type, w.department].some(v => v && String(v).toLowerCase().includes(this.searchTerm.toLowerCase())))
          : [...this.warehouses];
        this.totalItems  = result.totalItems;
        this.totalPages  = result.totalPages;
        this.hasNextPage = this.currentPage < this.totalPages;
        this.hasPreviousPage = this.currentPage > 1;
        this.currentPage = result.page;
        this.pageSize    = result.pageSize;
        this.isLoading   = false;
      },
      error: (err) => {
        this.error = `Erro ao carregar almoxarifados: ${err.message || 'Erro desconhecido'}`;
        this.isLoading = false;
        this.warehouses = [];
        this.filteredWarehouses = [];
      }
    });
  }

  onSearch(term: string): void {
    this.searchTerm = term || '';
    this.applyFilters();
  }

  onActionClick(actionData: { action: string; item: any }): void {
    const { action, item } = actionData;
    switch (action) {
      case 'edit':
        this.edit(item);
        break;
      case 'delete':
        if (confirm(`Tem certeza que deseja excluir o almoxarifado "${item?.name}"?`)) {
          this.remove(item.id);
        }
        break;
      default:
    }
  }

  edit(warehouse: Warehouse): void {
    this.router.navigate(['/stock/warehouses', warehouse.id, 'edit']);
  }

  newWarehouse(): void {
    this.router.navigate(['/stock/warehouses/new']);
  }

  remove(id: number): void {
    this.warehousesService.delete(id).subscribe(() => this.loadWarehouses());
  }

  onSortChange(event: { sortBy: string; sortOrder: 'asc' | 'desc' }): void {
    this.filteredWarehouses = [...this.filteredWarehouses].sort((a, b) => {
      const va = String((a as any)[event.sortBy] ?? '');
      const vb = String((b as any)[event.sortBy] ?? '');
      const cmp = va.localeCompare(vb, 'pt-BR', { numeric: true });
      return event.sortOrder === 'asc' ? cmp : -cmp;
    });
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.applyFilters();
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.currentPage = 1;
    this.applyFilters();
  }
}
